/**
 * HealthMonitorPage (P3-F Doc 41) — realtime status grid 6 components.
 */
import {
  AlertTriangle,
  CheckCircle2,
  Cloud,
  Database,
  HardDrive,
  Loader2,
  RefreshCw,
  Search,
  Server,
  XCircle,
} from "lucide-react";
import * as React from "react";

import { healthApi, type HealthReport, type ComponentStatus } from "@/api/health";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useUIStore } from "@/stores/ui";

const REFRESH_INTERVAL_MS = 10_000;

const COMPONENT_META: Record<
  string,
  { label: string; Icon: typeof Database; description: string }
> = {
  database: {
    label: "Database",
    Icon: Database,
    description: "PostgreSQL primary store",
  },
  redis: {
    label: "Redis",
    Icon: Server,
    description: "Cache + queue broker",
  },
  qdrant: {
    label: "Qdrant",
    Icon: Search,
    description: "Vector DB cho RAG",
  },
  minio: {
    label: "MinIO / S3",
    Icon: HardDrive,
    description: "Object storage",
  },
  odoo: {
    label: "Odoo ERP",
    Icon: Cloud,
    description: "External ERP integration",
  },
  llm: {
    label: "LLM Providers",
    Icon: Cloud,
    description: "AI completion providers",
  },
};

export function HealthMonitorPage(): JSX.Element {
  const pushToast = useUIStore((s) => s.pushToast);
  const [report, setReport] = React.useState<HealthReport | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [autoRefresh, setAutoRefresh] = React.useState(true);

  const refresh = React.useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const r = await healthApi.components();
        setReport(r);
      } catch (e) {
        if (!silent) {
          pushToast({
            variant: "destructive",
            title: "Không tải được status",
            description: e instanceof Error ? e.message : String(e),
          });
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [pushToast],
  );

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  React.useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => {
      void refresh(true);
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [autoRefresh, refresh]);

  const components = report?.components ?? {};
  const checkedAt = report?.checked_at;

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-4">
      <header className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            System Health
            {report ? (
              report.ok ? (
                <Badge className="bg-emerald-500 hover:bg-emerald-600 text-white text-[10px]">
                  <CheckCircle2 className="size-3 mr-0.5" />
                  All systems operational
                </Badge>
              ) : (
                <Badge variant="destructive" className="text-[10px]">
                  <AlertTriangle className="size-3 mr-0.5" />
                  Degraded
                </Badge>
              )
            ) : null}
          </h1>
          <p className="text-sm text-muted-foreground">
            Realtime status 6 components. Auto-refresh mỗi 10s.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh
          </label>
          <Button
            size="sm"
            variant="outline"
            onClick={() => void refresh()}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="size-4 mr-1 animate-spin" />
            ) : (
              <RefreshCw className="size-4 mr-1" />
            )}
            Refresh
          </Button>
        </div>
      </header>

      {checkedAt ? (
        <p className="text-[10px] text-muted-foreground">
          Last checked: {new Date(checkedAt).toLocaleString("vi-VN")}
        </p>
      ) : null}

      {/* Component grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {Object.entries(COMPONENT_META).map(([key, meta]) => {
          const status = components[key];
          return (
            <ComponentRow
              key={key}
              componentKey={key}
              status={status}
              meta={meta}
            />
          );
        })}
      </div>
    </div>
  );
}

interface RowProps {
  componentKey: string;
  status: ComponentStatus | undefined;
  meta: (typeof COMPONENT_META)[string];
}

function ComponentRow({ componentKey, status, meta }: RowProps): JSX.Element {
  const Icon = meta.Icon;
  const ok = status?.ok ?? false;
  const isLLM = componentKey === "llm";

  return (
    <Card
      className={cn(
        "transition-colors",
        status === undefined && "opacity-60",
        ok && "border-emerald-300/60 dark:border-emerald-700/40",
        !ok && status !== undefined && "border-rose-300/60 dark:border-rose-700/40",
      )}
    >
      <CardContent className="p-3 space-y-2">
        <div className="flex items-start gap-2">
          <Icon
            className={cn(
              "size-5 shrink-0 mt-0.5",
              ok ? "text-emerald-600" : "text-muted-foreground",
            )}
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm">{meta.label}</span>
              {status === undefined ? (
                <Loader2 className="size-3 animate-spin text-muted-foreground" />
              ) : ok ? (
                <CheckCircle2 className="size-3.5 text-emerald-600" />
              ) : (
                <XCircle className="size-3.5 text-destructive" />
              )}
            </div>
            <p className="text-[11px] text-muted-foreground">
              {meta.description}
            </p>
          </div>
          {status?.latency_ms !== null && status?.latency_ms !== undefined ? (
            <span
              className={cn(
                "text-[10px] font-mono shrink-0",
                status.latency_ms > 200
                  ? "text-amber-600"
                  : "text-muted-foreground",
              )}
            >
              {status.latency_ms}ms
            </span>
          ) : null}
        </div>

        {/* LLM providers detail */}
        {isLLM && status?.detail?.providers ? (
          <ul className="space-y-0.5 text-[11px] pl-7">
            {Object.entries(
              status.detail.providers as Record<
                string,
                { available: boolean; label?: string; reason?: string | null }
              >,
            ).map(([id, p]) => (
              <li key={id} className="flex items-center gap-1.5">
                {p.available ? (
                  <CheckCircle2 className="size-3 text-emerald-600 shrink-0" />
                ) : (
                  <XCircle className="size-3 text-destructive shrink-0" />
                )}
                <span className="font-medium">{p.label ?? id}</span>
                {!p.available && p.reason ? (
                  <span className="text-muted-foreground truncate">
                    — {p.reason}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}

        {/* Detail badges */}
        {!isLLM && status?.detail ? (
          <div className="flex flex-wrap gap-1 pl-7">
            {Object.entries(status.detail).map(([k, v]) => {
              if (v === null || v === undefined) return null;
              const display = Array.isArray(v)
                ? `${k}: [${v.length}]`
                : `${k}: ${String(v).slice(0, 40)}`;
              return (
                <span
                  key={k}
                  className="text-[9px] bg-muted px-1.5 py-0.5 rounded"
                >
                  {display}
                </span>
              );
            })}
          </div>
        ) : null}

        {!ok && status?.error ? (
          <p className="text-[10px] text-destructive bg-destructive/5 border border-destructive/20 rounded px-2 py-1 line-clamp-2">
            {status.error}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
